import React from 'react';
import { Lightbulb, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useBoundStore } from '../store';
import { useI18n } from '../i18n';

interface HintProps {
  /** Stable id; once dismissed (per user) the tip never shows again. */
  id: string;
  title?: string;
  children: React.ReactNode;
  className?: string;
}

/**
 * A lightweight, one-time contextual tip. Renders nothing once the user has
 * dismissed it (persisted to the backend via the onboarding state). Place it
 * inline next to the feature it explains.
 */
export const Hint: React.FC<HintProps> = ({ id, title, children, className }) => {
  const { t } = useI18n();
  const dismissedTips = useBoundStore((s) => s.dismissedTips);
  const dismissTip = useBoundStore((s) => s.dismissTip);

  if (dismissedTips.includes(id)) return null;

  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded-lg border border-brand/30 bg-brand-light/40 px-3 py-2 text-sm',
        className,
      )}
    >
      <Lightbulb className="h-4 w-4 shrink-0 text-brand mt-0.5" />
      <div className="flex-1 min-w-0">
        {title && <div className="font-medium text-foreground">{title}</div>}
        <div className="text-muted-foreground">{children}</div>
      </div>
      <button
        type="button"
        onClick={() => dismissTip(id)}
        aria-label={t.onboarding.tips.dismiss}
        className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted-foreground/10"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

export default Hint;
